package vegasful.admin.views

import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import vegasful.admin.Application
import vegasful.admin.admin
import vegasful.admin.api.EventSearch
import vegasful.admin.api.EventUpdateInput
import vegasful.admin.api.client.Event
import vegasful.admin.api.client.Venue
import vegasful.admin.api.client.query
import vegasful.admin.components.*

/**
 * A minimal "create event" form. Events created here are typically parent
 * (umbrella) events for a series or festival — e.g. "Vegas Golden Knights" —
 * that individual loaded events are then grouped under via [assignChildren].
 *
 * Only a name is required. A venue is optional but recommended: it anchors the
 * umbrella to a venue and lets the "Assign Children" picker scope its search to
 * that venue's events. On submit the event is created through the standard
 * `admin.events.add` mutation and we navigate to the full edit view.
 */
@Composable
fun createEvent(venueId: String? = null) {
    var name by remember { mutableStateOf<String?>(null) }
    var selectedVenue by remember { mutableStateOf<Venue?>(null) }
    var venueCleared by remember { mutableStateOf(false) }

    // Preload the venue passed in via the route (e.g. from a venue page), if any.
    LaunchedEffect(venueId) {
        if (venueId != null) {
            selectedVenue = Application.api.query {
                venues {
                    venue(venueId) {
                        this.id
                        this.name
                        this.path
                    }
                }
            }.venues.venue
        }
    }

    suspend fun create() {
        val eventName = name?.trim()
        if (eventName.isNullOrBlank()) {
            Application.notifications.showError("A name is required to create an event.")
            return
        }
        val created = Application.api.admin("CreateEvent") {
            events {
                add(EventUpdateInput(name = eventName, venue = selectedVenue?.id)) {
                    this.id
                }
            }
        }.events.add
        Application.navigation.navigate("content/events/${created.id}")
    }

    contentContainer {
        entityView {
            title = "New Event"
            subTitle = "Create a parent or standalone event"

            breadcrumbs {
                crumb("Events", "content/events")
            }

            action {
                title = "Create"
                primary = true
                showProgressOnAction = true
                enabled = !name.isNullOrBlank()
                action { create() }
            }

            content {
                box("Basics", {}) {
                    dialogField(
                        "Name",
                        "The event name. For a series or festival this is the umbrella name (e.g. 'Vegas Golden Knights')."
                    ) {
                        simpleTextField(name) { name = it }
                    }

                    dialogField("Venue", "Optional, but recommended for grouping. Attach the event to a venue.") {
                        if (venueCleared || selectedVenue == null) {
                            venuesSearch({ true }) {
                                selectedVenue = it
                                venueCleared = false
                            }
                        } else {
                            selectedVenue?.let {
                                A(href = "#content/venues/${it.id}") { Text(it.name) }
                                icon("delete") {
                                    action {
                                        venueCleared = true
                                        selectedVenue = null
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * "Assign Children" — group a set of events under a common parent.
 *
 * This is the non-destructive counterpart to the destructive `events.merge`
 * mutation: instead of deleting sources, each selected event's parent is set to
 * this event (written on the override layer, so a later loader run won't clear
 * it). The picker is scoped to the parent's venue, since series children
 * (sports seasons, festival nights) almost always share the parent's venue.
 */
@Composable
fun assignChildren(parentId: String) {
    var parentName by remember { mutableStateOf<String?>(null) }
    var venue by remember { mutableStateOf<Venue?>(null) }
    var loaded by remember { mutableStateOf(false) }
    var candidates by remember { mutableStateOf<List<Event>?>(null) }
    val selected = remember { mutableStateMapOf<String, Boolean>() }
    var filter by remember { mutableStateOf("") }

    LaunchedEffect(parentId) {
        val resolved = Application.api.admin("AssignChildrenParent") {
            events {
                event(parentId) {
                    resolved {
                        this.id
                        this.name
                        this.venue {
                            this.id
                            this.name
                            this.path
                        }
                    }
                }
            }
        }.events.event.resolved
        parentName = resolved.name
        venue = resolved.venue

        val venueId = resolved.venue?.id
        if (venueId != null) {
            // No date range — grouping candidates are the venue's events that
            // don't yet have a parent, regardless of when they occur. (An
            // explicit wide window here is very expensive on the timebox index
            // and can time out the resolver.)
            val results = Application.api.query {
                venues {
                    venue(venueId) {
                        events(EventSearch(pageSize = 500)) {
                            this.events {
                                this.id
                                this.name
                                this.date
                                this.time
                                this.parent {
                                    this.id
                                }
                            }
                        }
                    }
                }
            }.venues.venue.events

            // Only events without a parent (and never the parent itself).
            candidates = results.events
                .filter { it.id != parentId && it.parent == null }
                .distinctBy { it.id }
                .sortedBy { it.date ?: "" }
        }
        loaded = true
    }

    suspend fun assign() {
        val ids = selected.filter { it.value }.keys.toList()
        if (ids.isEmpty()) return
        ids.forEach { childId ->
            Application.api.admin("AssignChild") {
                events {
                    event(childId) {
                        layer("override") {
                            update(EventUpdateInput(parent = parentId)) {
                                this.layerId
                            }
                        }
                    }
                }
            }
        }
        // Rebuild the parent so its resolved date range and children list refresh.
        Application.api.admin("RebuildParent") {
            events {
                event(parentId) {
                    rebuild()
                }
            }
        }
        Application.navigation.navigate("content/events/$parentId")
    }

    contentContainer {
        entityView {
            title = parentName?.let { "Assign Children to $it" }

            breadcrumbs {
                crumb("Events", "content/events") {
                    crumb(parentName ?: parentId, "content/events/$parentId")
                }
            }

            action {
                val count = selected.count { it.value }
                title = if (count > 0) "Assign ($count)" else "Assign"
                primary = true
                showProgressOnAction = true
                enabled = count > 0
                action { assign() }
            }

            content {
                val current = candidates
                when {
                    venue == null && loaded ->
                        box("Events", {}) {
                            boxMessage("This event has no venue, so there are no events to group. Set a venue on the event first.")
                        }

                    current == null -> box("Events", {}) { boxSpinner("Loading events…") }

                    else -> box({
                        title = "Events at ${venue?.name.orEmpty()}"
                        paddedContent = false
                    }) {
                        Div({ style { padding(12.px, 16.px) } }) {
                            simpleTextField(filter.ifBlank { null }) { filter = it.orEmpty() }
                        }

                        val filtered = current.filter {
                            filter.isBlank() || it.name.contains(filter, ignoreCase = true)
                        }

                        if (filtered.isEmpty()) {
                            boxMessage("No matching events.")
                        } else {
                            table<Event> {
                                items(filtered)

                                column {
                                    width = 30.px
                                    content { event ->
                                        Input(InputType.Checkbox) {
                                            checked(selected[event.id] == true)
                                            onInput { selected[event.id] = it.value }
                                        }
                                    }
                                }

                                column("Date") {
                                    width = 110.px
                                    content { event -> Text(event.date.orEmpty()) }
                                }

                                column("Event") {
                                    content { event ->
                                        A(href = "#content/events/${event.id}") { Text(event.name) }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
